import React, { useEffect, useState, useCallback } from "react";
import { apiTreeActivity } from "./api";
import { CustomIcon } from "../shared/CustomIcon";

// Reverse-chronological Activity Feed. Backend already filters by role.
export default function ActivityFeed({ refreshToken, onOpenMember }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const PAGE = 20;

  const load = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const start = reset ? 0 : offset;
      const res = await apiTreeActivity(PAGE, start);
      setTotal(res.total || 0);
      setItems(prev => reset ? (res.items || []) : [...prev, ...(res.items || [])]);
      setOffset(start + (res.items?.length || 0));
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, [offset]);

  // Reload on refreshToken change (e.g. after new follow-up logged).
  useEffect(() => { load(true); /* eslint-disable-next-line */ }, [refreshToken]);

  const hasMore = offset < total;
  return (
    <div data-testid="activity-feed" style={{ marginTop: 18 }}>
      <div style={{ height: 1, background: "linear-gradient(90deg, transparent, color-mix(in srgb, var(--kc-accent) 55%, transparent), transparent)", margin: "10px 0 14px" }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontFamily: "'Cinzel',serif", fontSize: 13, color: "var(--kc-accent)", letterSpacing: "0.10em", textTransform: "uppercase", fontWeight: 700 }}>
          Activity Feed
        </div>
        <div style={{ fontSize: 11, color: "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif" }}>{total} entries</div>
      </div>

      {items.length === 0 && !loading && (
        <div data-testid="activity-empty" style={{ color: "var(--kc-sub)", fontSize: 12, padding: "12px 8px" }}>
          No follow-ups have been logged in your visibility scope yet.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map(a => (
          <div key={a.id} data-testid={`act-${a.id}`}
               style={{ display: "flex", gap: 10, padding: "10px 12px", background: "var(--kc-surface)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, alignItems: "center" }}>
            {a.loggedByPhoto ? (
              <img src={a.loggedByPhoto} alt="" style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
            ) : (
              <div style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--kc-accent)", color: "#111", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                {((a.loggedByName || "?")[0] || "?").toUpperCase()}
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: "var(--kc-text)", lineHeight: 1.3 }}>
                <strong>{a.loggedByName || "Someone"}</strong>
                {a.loggedByLevelName && (
                  <span style={{ marginLeft: 6, fontSize: 10, color: "var(--kc-accent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent)", padding: "1px 6px", borderRadius: 999 }}>
                    {a.loggedByLevelName}
                  </span>
                )}
                {" "}
                <span style={{ color: "var(--kc-sub)" }}>
                  {a.method === "In-person" ? "visited" : "contacted"}
                </span>
                {" "}
                <strong>{a.memberName}</strong>
                <span style={{ marginLeft: 6, fontSize: 10, color: "var(--kc-sub)" }}>
                  · {a.memberLevelName} · {a.method}
                </span>
              </div>
              {a.notesSummary && (
                <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 2, fontFamily: "'Montserrat',sans-serif" }}>{a.notesSummary}</div>
              )}
              <div style={{ fontSize: 10, color: "var(--kc-meta)", marginTop: 2, display: "flex", alignItems: "center", gap: 10 }}>
                <span>{a.date}</span>
                {a.memberId && onOpenMember && (
                  <button data-testid={`act-view-${a.id}`}
                          onClick={() => onOpenMember(a.memberId)}
                          style={{ background: "none", border: "none", color: "var(--kc-accent)", cursor: "pointer", padding: 0, textDecoration: "underline dashed", fontFamily: "inherit", fontSize: "inherit" }}>
                    View Profile
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <div style={{ textAlign: "center", marginTop: 10 }}>
          <button className="btn btn-outline btn-sm" data-testid="activity-load-more" onClick={() => load(false)} disabled={loading}>
            {loading ? "Loading…" : "Load More"}
          </button>
        </div>
      )}
    </div>
  );
}
